import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  IconButton,
  TableSortLabel,
  CircularProgress,
  Alert,
  Pagination,
  Snackbar,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TextareaAutosize,
  Skeleton,
} from "@mui/material";
import {
  Search,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  Delete,
  Edit,
  Save,
  Cancel,
  Note,
  FileDownload,
} from "@mui/icons-material";

const API_BASE_URL = `${process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL}/api`;

const CustomerSupportTable = ({ sidebarOpen = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [deleteLoading, setDeleteLoading] = useState({});
  const [updateLoading, setUpdateLoading] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    submissionId: null,
    submissionName: "",
  });
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    ticketId: null,
    notes: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Status and Priority options
  const statusOptions = [
    { value: "open", label: "Open", color: "#4caf50" },
    { value: "closed", label: "Closed", color: "#f44336" },
    { value: "on_hold", label: "On Hold", color: "#ff9800" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low", color: "#4caf50" },
    { value: "medium", label: "Medium", color: "#ff9800" },
    { value: "high", label: "High", color: "#f44336" },
    { value: "urgent", label: "Urgent", color: "#9c27b0" },
  ];

  // Export to CSV function
  const exportToCSV = () => {
    try {
      setExportLoading(true);

      // Use filtered data for export
      const dataToExport = filteredData;

      if (dataToExport.length === 0) {
        setSnackbar({
          open: true,
          message: "No data available to export",
          severity: "warning",
        });
        return;
      }

      // Define CSV headers
      const headers = [
        "Ticket ID",
        "Name",
        "Email",
        "Phone",
        "Subject",
        "Issue",
        "Status",
        "Priority",
        "Additional Notes",
        "Created Date",
        "Updated Date",
      ];

      // Convert data to CSV format
      const csvData = dataToExport.map((row) => [
        row._id || "",
        row.name || "",
        row.email || "",
        row.phone || "",
        row.subject || "",
        row.issue || "",
        row.status || "",
        row.priority || "",
        row.additional_notes || "",
        row.created_at ? new Date(row.created_at).toLocaleString() : "",
        row.updated_at ? new Date(row.updated_at).toLocaleString() : "",
      ]);

      // Combine headers and data
      const fullCSVData = [headers, ...csvData];

      // Convert to CSV string
      const csvString = fullCSVData
        .map((row) =>
          row
            .map((field) => {
              // Escape quotes and wrap in quotes if contains comma, quote, or newline
              const stringField = String(field);
              if (
                stringField.includes(",") ||
                stringField.includes('"') ||
                stringField.includes("\n")
              ) {
                return `"${stringField.replace(/"/g, '""')}"`;
              }
              return stringField;
            })
            .join(",")
        )
        .join("\n");

      // Create and download file
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);

        // Generate filename with current date
        const currentDate = new Date().toISOString().split("T")[0];
        const filename = `customer-support-tickets-${currentDate}.csv`;
        link.setAttribute("download", filename);

        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: `CSV file exported successfully (${dataToExport.length} records)`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setSnackbar({
        open: true,
        message: `Export failed: ${error.message}`,
        severity: "error",
      });
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/customer-support`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle different response formats
      if (result.success && result.data) {
        setData(result.data);
        setPagination((prev) => ({
          ...prev,
          totalItems: result.data.length,
          totalPages: Math.ceil(result.data.length / prev.itemsPerPage),
        }));
      } else if (Array.isArray(result)) {
        setData(result);
        setPagination((prev) => ({
          ...prev,
          totalItems: result.length,
          totalPages: Math.ceil(result.length / prev.itemsPerPage),
        }));
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Failed to load data: ${err.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Update ticket status, priority, or notes
  const updateTicket = async (ticketId, updateData) => {
    try {
      setUpdateLoading((prev) => ({ ...prev, [ticketId]: true }));

      const response = await fetch(
        `${API_BASE_URL}/customer-support/${ticketId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Update local state with new data from server
      if (result.success && result.data) {
        setData((prevData) =>
          prevData.map((item) => (item._id === ticketId ? result.data : item))
        );
      } else {
        // Fallback to local update
        setData((prevData) =>
          prevData.map((item) =>
            item._id === ticketId
              ? { ...item, ...updateData, updated_at: new Date().toISOString() }
              : item
          )
        );
      }

      setSnackbar({
        open: true,
        message: "Ticket updated successfully",
        severity: "success",
      });

      return result;
    } catch (err) {
      console.error("Error updating ticket:", err);
      setSnackbar({
        open: true,
        message: `Update failed: ${err.message}`,
        severity: "error",
      });
      throw err;
    } finally {
      setUpdateLoading((prev) => ({ ...prev, [ticketId]: false }));
    }
  };

  // Handle status change directly from dropdown
  const handleStatusChange = async (ticketId, newStatus) => {
    await updateTicket(ticketId, { status: newStatus });
  };

  // Handle priority change directly from dropdown
  const handlePriorityChange = async (ticketId, newPriority) => {
    await updateTicket(ticketId, { priority: newPriority });
  };

  // Handle edit mode toggle
  const handleEditClick = (row) => {
    setEditingRow(row._id);
    setEditingData({
      status: row.status || "open",
      priority: row.priority || "low",
      additional_notes: row.additional_notes || "",
    });
  };

  // Handle save edit
  const handleSaveEdit = async (ticketId) => {
    try {
      await updateTicket(ticketId, editingData);
      setEditingRow(null);
      setEditingData({});
    } catch (err) {
      // Error already handled in updateTicket
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditingData({});
  };

  // Handle notes dialog
  const handleNotesClick = (ticketId, currentNotes) => {
    setNotesDialog({
      open: true,
      ticketId,
      notes: currentNotes || "",
    });
  };

  const handleNotesSave = async () => {
    try {
      await updateTicket(notesDialog.ticketId, {
        additional_notes: notesDialog.notes,
      });
      setNotesDialog({ open: false, ticketId: null, notes: "" });
    } catch (err) {
      // Error already handled in updateTicket
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.issue?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.priority?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Calculate pagination for filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    const filtered = filteredData;

    const totalPages = Math.ceil(filtered.length / pagination.itemsPerPage);

    // Update pagination state
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
    }));

    return filtered.slice(startIndex, endIndex);
  }, [filteredData, pagination.currentPage, pagination.itemsPerPage]);

  // Delete contact submission via API
  const deleteContactSubmission = async (id) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [id]: true }));

      const response = await fetch(`${API_BASE_URL}/customer-support/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove from local state
      setData((prevData) => prevData.filter((item) => item._id !== id));

      setSnackbar({
        open: true,
        message: "Support ticket deleted successfully",
        severity: "success",
      });

      // If current page becomes empty, go to previous page
      if (paginatedData.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      }
    } catch (err) {
      console.error("Error deleting support ticket:", err);
      setSnackbar({
        open: true,
        message: `Delete failed: ${err.message}`,
        severity: "error",
      });
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [id]: false }));
      setDeleteDialog({ open: false, submissionId: null, submissionName: "" });
    }
  };

  // Handle delete click
  const handleDeleteClick = (id, name) => {
    setDeleteDialog({
      open: true,
      submissionId: id,
      submissionName: name,
    });
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (deleteDialog.submissionId) {
      deleteContactSubmission(deleteDialog.submissionId);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, submissionId: null, submissionName: "" });
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  // Handle refresh - refetch data from API
  const handleRefresh = async () => {
    await fetchData();
    setSnackbar({
      open: true,
      message: "Data refreshed successfully",
      severity: "success",
    });
  };

  // Sort data locally
  const sortedData = useMemo(() => {
    return [...paginatedData].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      if (sortOrder === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  }, [paginatedData, sortOrder]);

  const handleSortChange = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  // Date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status chip color
  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((opt) => opt.value === status);
    return statusOption?.color || "#9048FB";
  };

  // Get priority chip color
  const getPriorityColor = (priority) => {
    const priorityOption = priorityOptions.find(
      (opt) => opt.value === priority
    );
    return priorityOption?.color || "#9048FB";
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 220 : 100;

  // Skeleton Row Component
  const SkeletonRow = () => (
    <TableRow
      sx={{
        "&:nth-of-type(even)": {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      <TableCell sx={{ minWidth: 200 }}>
        <Skeleton variant="text" width="85%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <Skeleton variant="text" width="80%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        <Skeleton variant="text" width="90%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 130 }}>
        <Skeleton variant="text" width="75%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 180 }}>
        <Skeleton variant="text" width="85%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        <Skeleton variant="text" width="95%" height={20} />
        <Skeleton variant="text" width="80%" height={20} />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Skeleton variant="rounded" width={100} height={32} />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Skeleton variant="rounded" width={100} height={32} />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <Skeleton variant="text" width="70%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <Skeleton variant="text" width="70%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 140, textAlign: "center" }}>
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "#fafafa",
        minHeight: "100vh",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        transition: "margin-left 0.3s ease",
        fontFamily: "Outfit",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#9048FB",
            fontWeight: 600,
            fontFamily: "Outfit",
          }}
        >
          Customer Support Tickets
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Export to CSV">
            <IconButton
              onClick={exportToCSV}
              disabled={exportLoading || loading}
              sx={{
                bgcolor: "#4caf50",
                color: "white",
                "&:hover": { bgcolor: "#45a049" },
                "&:disabled": {
                  bgcolor: "rgba(76, 175, 80, 0.3)",
                  color: "rgba(255, 255, 255, 0.5)",
                },
              }}
            >
              {exportLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileDownload />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                bgcolor: "#9048FB",
                color: "white",
                "&:hover": { bgcolor: "#7c3aed" },
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={fetchData}
              sx={{ fontFamily: "Outfit" }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Search and Sort Controls */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by ticket ID, name, email, phone, subject, issue, status, or priority..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            maxWidth: 700,
            backgroundColor: "white",
            "& .MuiInputBase-input": {
              fontFamily: "Outfit",
              fontSize: "16px",
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            Sort:
          </Typography>
          <IconButton
            onClick={handleSortChange}
            sx={{
              bgcolor: "#9048FB",
              color: "white",
              "&:hover": { bgcolor: "#7c3aed" },
            }}
          >
            {sortOrder === "desc" ? <ArrowDownward /> : <ArrowUpward />}
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            {sortOrder === "desc" ? "Newest" : "Oldest"}
          </Typography>
        </Box>
      </Box>

      {/* Data Table */}
      <Paper sx={{ overflow: "hidden", boxShadow: 2 }}>
        <TableContainer
          sx={{
            position: "relative",
            maxHeight: 700,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a1a1a1",
            },
          }}
        >
          <Table sx={{ minWidth: 1400 }}>
            <TableHead sx={{ bgcolor: "#9048FB" }}>
              <TableRow>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Ticket ID
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 260,
                    whiteSpace: "nowrap",
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 130,
                    whiteSpace: "nowrap",
                  }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 180,
                    whiteSpace: "nowrap",
                  }}
                >
                  Subject
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Issue
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Priority
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSortChange}
                    sx={{
                      color: "white !important",
                      fontFamily: "Outfit",
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                      },
                    }}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  Updated
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 140,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show skeleton rows when loading
                Array.from({ length: 8 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                      fontFamily: "Outfit",
                      fontSize: "16px",
                    }}
                  >
                    {searchTerm ? "No results found" : "No tickets available"}
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: "#f5f5f5",
                      },
                      "&:hover": {
                        bgcolor: "#f3f0ff",
                      },
                    }}
                  >
                    {/* Full Ticket ID */}
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Outfit",
                        fontSize: "14px",
                        color: "#9048FB",
                        wordBreak: "break-all",
                        maxWidth: 200,
                      }}
                    >
                      <Tooltip title={row._id}>
                        <span>{row._id || "N/A"}</span>
                      </Tooltip>
                    </TableCell>

                    {/* Name */}
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Outfit",
                        fontSize: "16px",
                      }}
                    >
                      {row.name || "N/A"}
                    </TableCell>

                    {/* Email */}
                    <TableCell
                      sx={{
                        color: "#9048FB",
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        wordBreak: "break-word",
                      }}
                    >
                      {row.email || "N/A"}
                    </TableCell>

                    {/* Phone */}
                    <TableCell
                      sx={{
                        fontFamily: "Outfit",
                        fontSize: "16px",
                      }}
                    >
                      {row.phone || "N/A"}
                    </TableCell>

                    {/* Subject */}
                    <TableCell>
                      <Tooltip title={row.subject || "N/A"}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Outfit",
                            fontSize: "16px",
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "default",
                          }}
                        >
                          {row.subject || "N/A"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Issue */}
                    <TableCell>
                      <Tooltip title={row.issue || "N/A"}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Outfit",
                            fontSize: "16px",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "default",
                          }}
                        >
                          {row.issue || "N/A"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {/* Status */}
                    <TableCell>
                      {editingRow === row._id ? (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={editingData.status || "open"}
                            onChange={(e) =>
                              setEditingData((prev) => ({
                                ...prev,
                                status: e.target.value,
                              }))
                            }
                            sx={{ fontFamily: "Outfit" }}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={row.status || "open"}
                            onChange={(e) =>
                              handleStatusChange(row._id, e.target.value)
                            }
                            disabled={updateLoading[row._id]}
                            sx={{ fontFamily: "Outfit" }}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      backgroundColor: option.color,
                                    }}
                                  />
                                  {option.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>

                    {/* Priority */}
                    <TableCell>
                      {editingRow === row._id ? (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={editingData.priority || "low"}
                            onChange={(e) =>
                              setEditingData((prev) => ({
                                ...prev,
                                priority: e.target.value,
                              }))
                            }
                            sx={{ fontFamily: "Outfit" }}
                          >
                            {priorityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={row.priority || "low"}
                            onChange={(e) =>
                              handlePriorityChange(row._id, e.target.value)
                            }
                            disabled={updateLoading[row._id]}
                            sx={{ fontFamily: "Outfit" }}
                          >
                            {priorityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      backgroundColor: option.color,
                                    }}
                                  />
                                  {option.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>

                    {/* Created Date */}
                    <TableCell
                      sx={{
                        color: "#A3AED0",
                        fontFamily: "Outfit",
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(row.created_at)}
                    </TableCell>

                    {/* Updated Date */}
                    <TableCell
                      sx={{
                        color: "#A3AED0",
                        fontFamily: "Outfit",
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(row.updated_at)}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        {editingRow === row._id ? (
                          <>
                            <Tooltip title="Save changes">
                              <IconButton
                                onClick={() => handleSaveEdit(row._id)}
                                disabled={updateLoading[row._id]}
                                sx={{
                                  color: "#4caf50",
                                  "&:hover": {
                                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                                  },
                                }}
                              >
                                {updateLoading[row._id] ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <Save fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel edit">
                              <IconButton
                                onClick={handleCancelEdit}
                                sx={{
                                  color: "#f44336",
                                  "&:hover": {
                                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                                  },
                                }}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit ticket">
                              <IconButton
                                onClick={() => handleEditClick(row)}
                                sx={{
                                  color: "#9048FB",
                                  "&:hover": {
                                    backgroundColor: "rgba(144, 72, 251, 0.1)",
                                  },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Additional notes">
                              <IconButton
                                onClick={() =>
                                  handleNotesClick(
                                    row._id,
                                    row.additional_notes
                                  )
                                }
                                sx={{
                                  color: "#ff9800",
                                  "&:hover": {
                                    backgroundColor: "rgba(255, 152, 0, 0.1)",
                                  },
                                }}
                              >
                                <Note fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete ticket">
                              <IconButton
                                onClick={() =>
                                  handleDeleteClick(row._id, row.name)
                                }
                                disabled={deleteLoading[row._id]}
                                sx={{
                                  color: "#f44336",
                                  "&:hover": {
                                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                                  },
                                  "&:disabled": {
                                    color: "rgba(244, 67, 54, 0.3)",
                                  },
                                }}
                              >
                                {deleteLoading[row._id] ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <Delete fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination and Results Summary */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Outfit", fontSize: "16px" }}
        >
          {loading ? (
            <Skeleton variant="text" width={200} height={24} />
          ) : (
            <>
              Showing {sortedData.length} of {pagination.totalItems} tickets
              {searchTerm && ` (filtered by "${searchTerm}")`}
            </>
          )}
        </Typography>

        {pagination.totalPages > 1 && !loading && (
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#9048FB",
                fontFamily: "Outfit",
                fontSize: "16px",
                "&.Mui-selected": {
                  backgroundColor: "#9048FB",
                  color: "white",
                },
                "&:hover": {
                  backgroundColor: "#f3f0ff",
                },
              },
            }}
            disabled={loading}
          />
        )}
      </Box>

      {/* Notes Dialog */}
      <Dialog
        open={notesDialog.open}
        onClose={() =>
          setNotesDialog({ open: false, ticketId: null, notes: "" })
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: "Outfit",
            fontWeight: 600,
            color: "#9048FB",
          }}
        >
          Additional Notes
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: "Outfit",
              fontSize: "14px",
              color: "text.secondary",
              mb: 2,
            }}
          >
            Add or update additional notes for this support ticket.
          </DialogContentText>
          <TextareaAutosize
            minRows={4}
            maxRows={8}
            value={notesDialog.notes}
            onChange={(e) =>
              setNotesDialog((prev) => ({ ...prev, notes: e.target.value }))
            }
            placeholder="Enter additional notes for this ticket..."
            style={{
              width: "100%",
              padding: "12px",
              fontFamily: "Outfit",
              fontSize: "16px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              resize: "vertical",
              outline: "none",
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() =>
              setNotesDialog({ open: false, ticketId: null, notes: "" })
            }
            sx={{
              fontFamily: "Outfit",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNotesSave}
            variant="contained"
            disabled={updateLoading[notesDialog.ticketId]}
            sx={{
              fontFamily: "Outfit",
              backgroundColor: "#9048FB",
              "&:hover": {
                backgroundColor: "#7c3aed",
              },
            }}
          >
            {updateLoading[notesDialog.ticketId] ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save Notes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 600,
            color: "#f44336",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontFamily: "Outfit",
              fontSize: "16px",
            }}
          >
            Are you sure you want to delete the ticket from{" "}
            <strong>{deleteDialog.submissionName}</strong>? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              fontFamily: "Outfit",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              backgroundColor: "#f44336",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontFamily: "Outfit",
            fontSize: "16px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerSupportTable;
